import React from 'react';
import {useFetchLocations} from '../../hooks';
import {useGuestFormEnv} from '../../features/guestForm/env';
import {countriesFilter, Select, type SingleSelectProps} from '@chekinapp/ui';
import {SelectOption} from '@chekin/core';
import {POPULAR_COUNTRY_CODES, RECENT_COUNTRIES_STORAGE_KEY} from './constants';

type CountryOption = SelectOption;

type Props = Omit<
  SingleSelectProps<unknown, string, string>,
  'options' | 'popularValues'
> & {
  priorityCodes?: Array<string | null | undefined>;
};

function getPopularValues(priorityCodes?: Array<string | null | undefined>) {
  const leading = (priorityCodes ?? []).filter((code): code is string => Boolean(code));
  return Array.from(new Set([...leading, ...POPULAR_COUNTRY_CODES]));
}

const CountriesSelectInternal = (
  {showRecent, recentStorageKey, priorityCodes, ...props}: Props,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const {locationsAsOptions} = useFetchLocations();
  const {showRecentCountries = true} = useGuestFormEnv();
  const priorityKey = (priorityCodes ?? []).join(',');
  const popularValues = React.useMemo(
    () => getPopularValues(priorityKey ? priorityKey.split(',') : undefined),
    [priorityKey],
  );

  return (
    <Select<unknown, string, string>
      {...props}
      filterOption={countriesFilter}
      ref={ref}
      options={locationsAsOptions as CountryOption[]}
      popularValues={popularValues}
      showRecent={showRecent ?? showRecentCountries}
      recentStorageKey={recentStorageKey ?? RECENT_COUNTRIES_STORAGE_KEY}
    />
  );
};

export const CountriesSelect = React.forwardRef(CountriesSelectInternal) as (
  props: Props & {ref?: React.ForwardedRef<HTMLDivElement>},
) => ReturnType<typeof CountriesSelectInternal>;
